import React, { useContext, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Box, Button, CircularProgress, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AuthorizationContext } from 'src/context/AuthorizationProvider';

const UserCustomerEnrollment = () => {
  const { customerId, userData, setUserData } = useContext(AuthorizationContext);
  const { accounts } = useMsal();

  const [cua, setCua] = useState('');
  const [cuaError, setCuaError] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('activation');

  // CUA format validation regex
  const cuaRegex = /^CUA-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

  const validateCua = value => {
    if (!value) {
      setCuaError(t('cuaRequired'));
      return false;
    }
    if (!cuaRegex.test(value)) {
      setCuaError(t('invalidCuaFormat', { fallback: 'Invalid format. Please use CUA-XXXX-XXXX-XXXX' }));
      return false;
    }
    setCuaError('');
    return true;
  };

  const handleCuaChange = event => {
    const value = event.target.value.toUpperCase();
    setCua(value);
    // Only validate if there's a value to avoid showing errors initially
    if (value) {
      validateCua(value);
    } else {
      setCuaError('');
    }
  };

  const handleClick = async () => {
    if (!validateCua(cua)) {
      return;
    }

    setLoading(true);
    try {
      const updateUserResponse = await axios.patch(`${process.env.REACT_APP_API_URL}/property/${customerId}/user/${accounts[0].idTokenClaims.sub}`, {
        cua
      });
      const userDetails = userData?.details || [];
      const userCustomerDetail = userDetails.find(detail => detail.customerId === customerId);
      if (updateUserResponse.data?.successInd && userCustomerDetail) {
        const responseUserCustomerDetail = updateUserResponse.data?.user?.details?.find(detail => detail.customer_id === customerId);
        userCustomerDetail.cua = responseUserCustomerDetail?.cua;

        // Show success toast notification
        toast.success(t('validationSuccess'));
      }
      const user = {
        ...userData,
        details: userDetails
      };
      setUserData(user);
      localStorage.setItem('localUser', JSON.stringify(user));
    } catch (error) {
      console.error('Error validating CUA:', error);
      // Show error toast notification
      toast.error(t('codeValidationError', { codeName: 'CUA' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : '33.33%'
      }}
    >
      {!userData?.details?.[0]?.cua ? (
        <>
          <Typography>{t('noActiveLicensesFound')}</Typography>
          <TextField
            error={!!cuaError}
            fullWidth
            helperText={cuaError}
            inputProps={{
              maxLength: 18
            }}
            label={t('cua')}
            margin="normal"
            onChange={handleCuaChange}
            placeholder="CUA-XXXX-XXXX-XXXX"
            value={cua}
            variant="outlined"
          />
          <Button color="primary" disabled={loading || !!cuaError || !cua} fullWidth onClick={handleClick} variant="contained">
            {loading ? <CircularProgress size={20} /> : t('validateCode')}
          </Button>
        </>
      ) : (
        <>
          {userData?.details?.[0]?.customerName && (
            <Typography sx={{ marginBottom: '15px' }} variant="h5">
              {t('welcomeMessage', { propertyName: userData?.details?.[0]?.customerName })}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default UserCustomerEnrollment;
